import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  alpha,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  Warehouse,
  Check,
  KeyboardArrowDown,
} from '@mui/icons-material';
import { BranchType } from '../../../interfaces/delivery-company/branches';
import { useSelectedBranch } from '../../../contexts/selected-branch-context';

interface BranchDropdownSelectorProps {
  onDrawerOpen?: () => void;
}

/**
 * Modern dropdown selector for branches that appears in the top app bar.
 * Shows current branch as a chip, and opens a dropdown to select other branches.
 * If only one branch is available, clicking opens the drawer instead.
 */
export const BranchDropdownSelector: React.FC<BranchDropdownSelectorProps> = ({
  onDrawerOpen,
}) => {
  const theme = useTheme();
  const { 
    selectedBranch, 
    setSelectedBranch, 
    availableBranches, 
    refreshAvailableBranches,
    isLoading 
  } = useSelectedBranch();
  
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Load branches on mount if not already loaded
  useEffect(() => {
    if (availableBranches.length === 0 && !isLoading && selectedBranch) {
      refreshAvailableBranches();
    }
  }, [selectedBranch]); // Only run when selectedBranch becomes available

  const handleChipClick = (event: React.MouseEvent<HTMLElement>) => {
    // If branches aren't loaded yet, try to refresh
    if (availableBranches.length === 0 && !isLoading) {
      refreshAvailableBranches().then((branches) => {
        if (branches.length > 1) {
          setAnchorEl(event.currentTarget);
        } else {
          onDrawerOpen?.();
        }
      });
      return;
    }
    
    if (availableBranches.length <= 1) {
      // If only one branch, open the drawer instead
      onDrawerOpen?.();
    } else {
      // If multiple branches, show dropdown
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleBranchSelect = (branch: BranchType) => {
    setSelectedBranch(branch);
    handleClose();
  };

  const open = Boolean(anchorEl);

  if (!selectedBranch) {
    return null;
  }

  return (
    <>
      <Chip
        icon={<Warehouse />}
        label={selectedBranch.name}
        onClick={handleChipClick}
        {...(availableBranches.length > 1 && {
          onDelete: (e) => {
            // Clicking the arrow should also open the dropdown
            e.stopPropagation();
            handleChipClick(e as unknown as React.MouseEvent<HTMLElement>);
          },
          deleteIcon: <KeyboardArrowDown />,
        })}
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
          },
        }}
      />

      {availableBranches.length > 1 && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              elevation: 8,
              sx: {
                mt: 1,
                borderRadius: 2,
                minWidth: 250,
                maxWidth: 350,
                overflow: 'hidden',
              },
            },
          }}
        >
          <Box sx={{ p: 2, pb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              SELECT BRANCH
            </Typography>
          </Box>
          <Divider />
          <List sx={{ p: 1, maxHeight: 400, overflow: 'auto' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              availableBranches.map((branch) => {
                const isSelected = branch.id === selectedBranch.id;
                return (
                  <ListItem key={branch.id} disablePadding>
                    <ListItemButton
                      onClick={() => handleBranchSelect(branch)}
                      selected={isSelected}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&.Mui-selected': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.12),
                          '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                          },
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {isSelected ? (
                          <Check color="primary" />
                        ) : (
                          <Warehouse sx={{ opacity: 0.5 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={branch.name}
                        secondary={branch.area?.name_en}
                        primaryTypographyProps={{
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? 'primary' : 'text.primary',
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })
            )}
          </List>
        </Popover>
      )}
    </>
  );
};

export default BranchDropdownSelector;

